type Props = {
  language?: "en" | "zh";
};

/**
 * Terms and Conditions tab (English + Simplified Chinese), replicated from the
 * icheck-chinese project. Re-mounts on `language` to replay the enter animation
 * via tailwindcss-animate.
 */
const TermsAndConditionsTab = ({ language = "en" }: Props) => {
  return (
    <div
      key={language}
      className="animate-in fade-in slide-in-from-right-10 duration-500 ease-in-out"
    >
      {language === "zh" ? <ChineseContent /> : <EnglishContent />}
    </div>
  );
};

const EnglishContent = () => (
  <>
    <h1 className="text-2xl font-bold mb-4">Terms and Conditions</h1>
    <p className="mb-4">
      These Terms and Conditions ("Terms") were last updated on August 22,
      2025.
    </p>
    <p className="mb-4">
      Please review these Terms carefully as they serve as an enforceable
      contract between us and contain important information about your legal
      rights, remedies, and obligations. iCheck is a compliance and inspection
      management platform owned by Element Tree. Element Tree’s mission is to
      provide top-quality compliance management solutions through iCheck.
      These Terms apply to all your activities on the iCheck website, mobile
      applications, and other related services (“Services”).
    </p>
    <p className="mb-4">
      By using our Services, you agree to these Terms. If you do not agree, do
      not use our Services.
    </p>
    <h2 className="text-xl font-semibold mb-2">Table of Contents</h2>
    <ul className=" pl-6 mb-4">
      <li>
        <a href="#accounts">1. Accounts</a>
      </li>
      <li>
        <a href="#contentEnrolment">
          2. Content Enrollment and Lifetime Access
        </a>
      </li>
      <li>
        <a href="#payment">3. Payments and Refunds</a>
      </li>
      <li>
        <a href="#contentBeheviour">4. Content and Behavior Rules</a>
      </li>
      <li>
        <a href="#rights">5. iCheck’s Rights to Content You Post</a>
      </li>
      <li>
        <a href="#ownrisk">6. Using iCheck at Your Own Risk</a>
      </li>
      <li>
        <a href="#Rights">7. iCheck’s Rights</a>
      </li>
      <li>
        <a href="#Legalterms">8. Miscellaneous Legal Terms</a>
      </li>
      <li>
        <a href="#Resolutions">9. Dispute Resolution</a>
      </li>
      <li>
        <a href="#Updating">10. Updating These Terms</a>
      </li>
      <li>
        <a href="#contact">11. How to Contact Us</a>
      </li>
    </ul>

    <h2 id="accounts" className="text-xl font-semibold mb-2">
      1. Accounts
    </h2>
    <p className="mb-4">
      You need an account for most activities on our platform. Keep your
      password secure because you’re responsible for all activity associated
      with your account. If you suspect someone else is using your account,
      let us know by contacting our Support Team. You must be of legal age to
      use iCheck.
    </p>
    <p className="mb-4">
      You must provide accurate and complete information when setting up and
      maintaining your account. You have complete responsibility for your
      account and everything that happens on your account, including any harm
      or damage caused by someone using your account without your permission.
      You may not transfer your account to someone else or use someone else’s
      account. If you contact us to request access to an account, we will not
      grant you such access unless you can provide us with the information we
      need to prove you are the owner of that account. You may terminate your
      account at any time by following the steps detailed in our Privacy
      Policy.
    </p>

    <h2 id="contentEnrolment" className="text-xl font-semibold mb-2">
      2. Content Enrollment and Lifetime Access
    </h2>
    <p className="mb-4">
      When you enroll in a course or use any content, you get a license from
      us to view it via the iCheck Services for compliance and inspection
      purposes, and no other use. Don’t try to transfer or resell content in
      any way. We generally grant you lifetime access to the content, except
      when we must disable the content because of legal or policy reasons.
    </p>
    <p className="mb-4">
      As a user, when you enroll or purchase content, you are getting a
      license from iCheck to view the content via our platform and Services.
      This license does not give you any right to resell the content in any
      manner. iCheck grants you a limited, non-exclusive, non-transferable
      license to access and view the content you have paid for, solely for
      your compliance purposes and in accordance with these Terms. We
      generally offer lifetime access, but we reserve the right to revoke any
      license to access content for legal or policy reasons.
    </p>

    <h2 id="payment" className="text-xl font-semibold mb-2">
      3. Payments and Refunds
    </h2>
    <p className="mb-4">
      When you make a payment, you agree to use a valid payment method. If you
      aren’t satisfied with the content, iCheck offers a 30-day refund or
      credit for most content purchases.
    </p>

    <h3 className="text-lg font-semibold mb-2">3.1 Pricing</h3>
    <p className="mb-4">
      The prices of content on iCheck are determined based on our pricing
      policies. In some instances, the price of content offered on iCheck may
      not be exactly the same as the price offered on our mobile or TV
      applications due to platform providers’ pricing systems and their
      policies around implementing sales and promotions. We occasionally run
      promotions and sales for our content, during which certain content is
      available at discounted prices for a set period.
    </p>

    <h3 className="text-lg font-semibold mb-2">3.2 Payments</h3>
    <p className="mb-4">
      You agree to pay the fees for content you purchase, and you authorize us
      to charge your debit or credit card or process other means of payment
      for those fees. iCheck works with payment service providers to offer you
      the most convenient payment methods in your country and to keep your
      payment information secure.
    </p>

    <h3 className="text-lg font-semibold mb-2">
      3.3 Refunds and Refund Credits
    </h3>
    <p className="mb-4">
      If the content you purchased is not what you expected, you can request a
      refund within 30 days of your purchase. We reserve the right to apply
      your refund as a refund credit or a refund to your original payment
      method. Refunds are not applicable after the 30-day period, except if
      the content is disabled for legal or policy reasons or in cases of
      suspected fraud.
    </p>

    <h2 id="contentBeheviour" className="text-xl font-semibold mb-2">
      4. Content and Behavior Rules
    </h2>
    <p className="mb-4">
      You are solely responsible for all content you post on the platform. You
      must adhere to our Trust & Safety Guidelines and respect the
      intellectual property rights of others. We reserve the right to ban your
      account for repeated or major offenses.
    </p>

    <h2 id="rights" className="text-xl font-semibold mb-2">
      5. iCheck’s Rights to Content You Post
    </h2>
    <p className="mb-4">
      You retain ownership of the content you post, but by posting, you grant
      iCheck the right to share, distribute, and promote your content across
      our platform and partners.
    </p>

    <h2 id="ownrisk" className="text-xl font-semibold mb-2">
      6. Using iCheck at Your Own Risk
    </h2>
    <p className="mb-4">
      iCheck does not guarantee the reliability, validity, accuracy, or
      truthfulness of content provided by users. You use the Services at your
      own risk and agree to indemnify us for any claims arising from your use.
    </p>

    <h2 id="Rights" className="text-xl font-semibold mb-2">
      7. iCheck’s Rights
    </h2>
    <p className="mb-4">
      We own the iCheck platform, including the website, mobile applications,
      and related software, and we retain all rights, titles, and interests to
      our intellectual property.
    </p>

    <h2 id="Legalterms" className="text-xl font-semibold mb-2">
      8. Miscellaneous Legal Terms
    </h2>
    <p className="mb-4">
      These Terms represent an agreement between you and iCheck, governed by
      the laws of India. Any disputes will be resolved in the courts in
      Mumbai, India.
    </p>

    <h2 id="Resolutions" className="text-xl font-semibold mb-2">
      9. Dispute Resolution
    </h2>
    <p className="mb-4">
      Any disputes shall first attempt to be resolved informally, and if not
      resolved, through binding arbitration in accordance with applicable
      laws.
    </p>

    <h2 id="Updating" className="text-xl font-semibold mb-2">
      10. Updating These Terms
    </h2>
    <p className="mb-4">
      iCheck may update these Terms as necessary to reflect changes in our
      practices. We will notify users of any material changes through
      prominent means.
    </p>

    <h2 id="contact" className="text-xl font-semibold mb-2">
      11. How to Contact Us
    </h2>
    <p className="mb-4">
      For any questions or concerns regarding these Terms, please contact us
      at{" "}
      <a href="mailto:support@elementree.co.in" className="text-primary">
        support@elementree.co.in
      </a>
      .
    </p>
  </>
);

const ChineseContent = () => (
  <>
    <h1 className="text-2xl font-bold mb-4">条款与条件</h1>
    <p className="mb-4">
      本条款与条件("条款")最后更新日期为 2025 年 8 月 22 日。
    </p>
    <p className="mb-4">
      请仔细阅读本条款,因为它们是我们之间具有强制执行效力的合同,并包含有关您的合法权利、补救措施和义务的重要信息。iCheck 是由 Element Tree 拥有的合规与检查管理平台。Element Tree 的使命是通过 iCheck 提供顶级合规管理解决方案。本条款适用于您在 iCheck 网站、移动应用程序及其他相关服务("服务")上的所有活动。
    </p>
    <p className="mb-4">
      使用我们的服务即表示您同意本条款。如果您不同意,请勿使用我们的服务。
    </p>
    <h2 className="text-xl font-semibold mb-2">目录</h2>
    <ul className=" pl-6 mb-4">
      <li>
        <a href="#accounts">1. 账户</a>
      </li>
      <li>
        <a href="#contentEnrolment">2. 内容注册与终身访问</a>
      </li>
      <li>
        <a href="#payment">3. 付款与退款</a>
      </li>
      <li>
        <a href="#contentBeheviour">4. 内容与行为规则</a>
      </li>
      <li>
        <a href="#rights">5. iCheck 对您发布内容的权利</a>
      </li>
      <li>
        <a href="#ownrisk">6. 使用 iCheck 的风险自担</a>
      </li>
      <li>
        <a href="#Rights">7. iCheck 的权利</a>
      </li>
      <li>
        <a href="#Legalterms">8. 其他法律条款</a>
      </li>
      <li>
        <a href="#Resolutions">9. 争议解决</a>
      </li>
      <li>
        <a href="#Updating">10. 更新本条款</a>
      </li>
      <li>
        <a href="#contact">11. 如何联系我们</a>
      </li>
    </ul>

    <h2 id="accounts" className="text-xl font-semibold mb-2">
      1. 账户
    </h2>
    <p className="mb-4">
      在我们平台上的大多数活动都需要账户。请妥善保管您的密码,因为您需对与您账户相关的所有活动负责。如果您怀疑他人正在使用您的账户,请通过联系我们的支持团队告知我们。您必须达到法定年龄方可使用 iCheck。
    </p>
    <p className="mb-4">
      在创建和维护您的账户时,您必须提供准确完整的信息。您对您的账户及账户上发生的一切承担全部责任,包括他人未经您许可使用您的账户所造成的任何伤害或损害。您不得将您的账户转让给他人,也不得使用他人的账户。如果您联系我们请求访问某账户,除非您能提供我们所需的信息以证明您是该账户的所有者,否则我们将不会授予您此类访问权限。您可以随时按照我们《隐私政策》中详细列出的步骤终止您的账户。
    </p>

    <h2 id="contentEnrolment" className="text-xl font-semibold mb-2">
      2. 内容注册与终身访问
    </h2>
    <p className="mb-4">
      当您注册课程或使用任何内容时,您将从我们处获得许可,以便通过 iCheck 服务出于合规和检查目的查看该内容,而不得用于其他用途。请勿以任何方式尝试转让或转售内容。我们通常授予您对内容的终身访问权,但因法律或政策原因必须停用内容时除外。
    </p>
    <p className="mb-4">
      作为用户,当您注册或购买内容时,您将获得 iCheck 的许可,通过我们的平台和服务查看该内容。该许可不授予您以任何方式转售内容的权利。iCheck 授予您有限的、非独占的、不可转让的许可,以仅出于您的合规目的并根据本条款,访问和查看您已付费的内容。我们通常提供终身访问权,但保留出于法律或政策原因撤销任何访问内容许可的权利。
    </p>

    <h2 id="payment" className="text-xl font-semibold mb-2">
      3. 付款与退款
    </h2>
    <p className="mb-4">
      付款时,您同意使用有效的付款方式。如果您对内容不满意,iCheck 为大多数内容购买提供 30 天退款或抵用额度。
    </p>

    <h3 className="text-lg font-semibold mb-2">3.1 定价</h3>
    <p className="mb-4">
      iCheck 上内容的价格根据我们的定价政策确定。在某些情况下,iCheck 上提供的内容价格可能与我们移动或电视应用程序上提供的价格不完全相同,这是由于平台提供商的定价系统及其实施销售和促销的政策所致。我们偶尔会针对内容进行促销和销售活动,在此期间某些内容将以折扣价在特定时间段内提供。
    </p>

    <h3 className="text-lg font-semibold mb-2">3.2 付款</h3>
    <p className="mb-4">
      您同意支付您所购买内容的费用,并授权我们从您的借记卡或信用卡扣款,或通过其他付款方式处理此类费用。iCheck 与支付服务提供商合作,为您提供贵国最便捷的付款方式,并保障您的付款信息安全。
    </p>

    <h3 className="text-lg font-semibold mb-2">3.3 退款与退款抵用额度</h3>
    <p className="mb-4">
      如果您购买的内容与您预期不符,您可以在购买后 30 天内申请退款。我们保留以退款抵用额度或退至您原付款方式形式退款的权利。30 天期限之后不再适用退款,但因法律或政策原因停用内容,或在涉嫌欺诈的情况下除外。
    </p>

    <h2 id="contentBeheviour" className="text-xl font-semibold mb-2">
      4. 内容与行为规则
    </h2>
    <p className="mb-4">
      您对在平台上发布的所有内容承担全部责任。您必须遵守我们的信任与安全指南,并尊重他人的知识产权。我们保留因重复或重大违规行为而封禁您账户的权利。
    </p>

    <h2 id="rights" className="text-xl font-semibold mb-2">
      5. iCheck 对您发布内容的权利
    </h2>
    <p className="mb-4">
      您保留对所发布内容的所有权,但发布即表示您授予 iCheck 在我们的平台及合作伙伴中分享、分发和推广您内容的权利。
    </p>

    <h2 id="ownrisk" className="text-xl font-semibold mb-2">
      6. 使用 iCheck 的风险自担
    </h2>
    <p className="mb-4">
      iCheck 不保证用户提供的内容的可靠性、有效性、准确性或真实性。您使用本服务的风险自担,并同意就因您的使用而产生的任何索赔向我们作出赔偿。
    </p>

    <h2 id="Rights" className="text-xl font-semibold mb-2">
      7. iCheck 的权利
    </h2>
    <p className="mb-4">
      我们拥有 iCheck 平台,包括网站、移动应用程序和相关软件,并保留对我们知识产权的全部权利、所有权和利益。
    </p>

    <h2 id="Legalterms" className="text-xl font-semibold mb-2">
      8. 其他法律条款
    </h2>
    <p className="mb-4">
      本条款代表您与 iCheck 之间的协议,受印度法律管辖。任何争议将在印度孟买的法院解决。
    </p>

    <h2 id="Resolutions" className="text-xl font-semibold mb-2">
      9. 争议解决
    </h2>
    <p className="mb-4">
      任何争议应首先尝试非正式解决,如未能解决,则根据适用法律通过具有约束力的仲裁解决。
    </p>

    <h2 id="Updating" className="text-xl font-semibold mb-2">
      10. 更新本条款
    </h2>
    <p className="mb-4">
      iCheck 可能会根据需要更新本条款,以反映我们做法的变化。我们将通过显著的方式通知用户任何重大变更。
    </p>

    <h2 id="contact" className="text-xl font-semibold mb-2">
      11. 如何联系我们
    </h2>
    <p className="mb-4">
      如对本条款有任何疑问或疑虑,请通过{" "}
      <a href="mailto:support@elementree.co.in" className="text-primary">
        support@elementree.co.in
      </a>{" "}
      与我们联系。
    </p>
  </>
);

export default TermsAndConditionsTab;
